import {
  Modal,
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Grid,
} from "@mui/material"
import { z } from "zod"
import { useAddModel } from "@/hooks/useAddModel"
import ClearIcon from "@mui/icons-material/Clear"
import { useEffect } from "react"

const CustomerSchema = z.object({
  name: z.string().min(3),
  email: z
    .string()
    .optional()
    .transform((val) => (val === "" ? undefined : val)),
  phone: z
    .string()
    .length(10, { message: "Phone number should contain 10 digits" })
    .optional()
    .transform((val) => (val === "" ? undefined : val)),
  address: z
    .string()
    .optional()
    .transform((val) => (val === "" ? undefined : val)),
})

type CustomerFormValue = z.infer<typeof CustomerSchema>

interface AddContactModalProps {
  open?: boolean
  onClose?: () => void
  defaultValues?: Partial<CustomerFormValue>
  getNewRecord?: (response: CustomerFormValue) => void
}

const AddContactModal: React.FC<AddContactModalProps> = ({
  open,
  onClose,
  defaultValues = {},
  getNewRecord,
}) => {
  const {
    errors,
    openDialog,
    isSubmitting,
    selectedRow,
    newRecord,
    register,
    handleSubmit,
    onSubmit,
    setValue,
    handleReset,
    defaultOnClose,
  } = useAddModel<CustomerFormValue>(
    "customer",
    CustomerSchema,
    "customer",
    onClose
  )

  useEffect(() => {
    if (defaultValues) {
      Object.entries(defaultValues).forEach(([key, value]) => {
        setValue(key as keyof CustomerFormValue, value as any)
      })
    }
  }, [defaultValues, setValue])

  useEffect(() => {
    if (newRecord && getNewRecord) {
      getNewRecord(newRecord)
    }
  }, [newRecord, getNewRecord])

  return (
    <Modal open={open ? open : openDialog} onClose={defaultOnClose}>
      <Box
        sx={{
          width: { xs: "95vw", sm: "90vw", md: 500 },
          margin: { xs: "50px auto", md: "100px auto" },
          padding: { xs: 1, sm: 2 },
          bgcolor: "white",
          borderRadius: 2,
          boxShadow: 3,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "#f6f7f9",
            padding: { xs: "15px 20px", md: "25px" },
            flexWrap: "wrap",
          }}
          className="rmu-modal-header"
        >
          <Typography
            id="modal-modal-title"
            variant="h6"
            sx={{
              fontWeight: "600",
              fontSize: { xs: "16px", md: "18px" },
              color: "black",
            }}
          >
            {selectedRow ? "Update Customer" : "Add New Customer"}
          </Typography>
          <IconButton
            aria-label="remove"
            size="small"
            onClick={onClose ? onClose : defaultOnClose}
          >
            <ClearIcon />
          </IconButton>
        </Box>

        {/* Form */}
        <Box
          sx={{ padding: 2 }}
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              fontSize: { xs: "12px", md: "16px" },
              marginBottom: 2,
              color: "black",
            }}
          >
            Basic Information
          </Typography>

          <TextField
            fullWidth
            label="Name"
            sx={{ marginBottom: 2 }}
            error={!!errors.name}
            helperText={errors.name?.message?.toString() || ""}
            {...register("name")}
          />

          <TextField
            fullWidth
            label="Email"
            sx={{ marginBottom: 2 }}
            error={!!errors.email}
            helperText={errors.email?.message?.toString() || ""}
            {...register("email")}
          />

          <TextField
            fullWidth
            label="Phone"
            sx={{ marginBottom: 2 }}
            error={!!errors.phone}
            helperText={errors.phone?.message?.toString() || ""}
            {...register("phone")}
          />

          <TextField
            fullWidth
            label="Address (optional)"
            sx={{ marginBottom: 2 }}
            error={!!errors.address}
            helperText={errors.address?.message?.toString() || ""}
            {...register("address")}
          />

          {/* Buttons */}
          <Grid item xs={12} mt={1}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 2,
              }}
            >
              <Button
                onClick={handleReset}
                variant="text"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: 500,
                  fontSize: "13px",
                  px: 3,
                  py: 1.5,
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                Reset
              </Button>

              <Button
                onClick={onClose ? onClose : defaultOnClose}
                variant="outlined"
                color="error"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: 500,
                  fontSize: "13px",
                  px: 3,
                  py: 1.5,
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                disabled={isSubmitting}
                variant="contained"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  fontWeight: 500,
                  fontSize: "13px",
                  px: 3,
                  py: 1.5,
                  width: { xs: "100%", sm: "auto" },
                  color: "#fff !important",
                }}
              >
                {selectedRow ? "Update" : "Create"}
              </Button>
            </Box>
          </Grid>
        </Box>
      </Box>
    </Modal>
  )
}

export default AddContactModal
